import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Building2,
  MapPin,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Users,
  GraduationCap,
  AlertTriangle
} from 'lucide-react';
import Header from '../../../../components/header/Header';
import Footer from '../../../../components/footer/Footer';
import HeroCarousel from '../../../../components/carousel/HeroCarousel';
import KVSLoader from '../../../../components/loaders/KVSLoader';
import Breadcrumb from '../../../../shared/components/Breadcrumb';
import img1 from '../../../../assets/attendenceCarousel.png';
import { attendanceApi } from '../../services/api';
import { DateRangePicker } from '../../components/analytics';
import { MAX_ANALYTICS_DATE_RANGE_DAYS, clampToDate } from '../../../../shared/utils/dateUtils';

interface KVSchoolData {
  kvID: number;
  schoolName: string;
  stateName: string;
  totalEnrollment: number;
  totalPresent: number;
  totalAbsent: number;
  averagePresent: number;
  attendancePercentage: number;
}

interface ROWiseDataResponse {
  success: boolean;
  summary?: {
    totalSchools: number;
    totalStudents: number;
  };
  data: Array<{
    roID: number;
    roName: string;
    kvs: KVSchoolData[];
  }>;
  pagination?: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  message?: string;
}

const StudentKVAttendanceList: React.FC = () => {
  const navigate = useNavigate();
  const { roId } = useParams<{ roId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const carouselImages = [img1];

  // Get query params
  const roName = searchParams.get('name') || '';
  const fromDate = searchParams.get('fromDate') || '';
  const toDate = searchParams.get('toDate') || '';

  // Initialize/Default date range query parameters if they are missing
  useEffect(() => {
    if (!searchParams.get('fromDate') || !searchParams.get('toDate')) {
      const today = new Date().toISOString().split('T')[0];
      const defaultFrom = (() => {
        const d = new Date(); d.setDate(d.getDate() - 7); return d.toISOString().split('T')[0];
      })();
      const defaultTo = clampToDate(defaultFrom, today);

      const newParams = new URLSearchParams(searchParams);
      if (!newParams.get('fromDate')) newParams.set('fromDate', defaultFrom);
      if (!newParams.get('toDate')) newParams.set('toDate', defaultTo);
      setSearchParams(newParams);
    }
  }, [searchParams, setSearchParams]);

  // State
  const [kvData, setKvData] = useState<KVSchoolData[]>([]);
  const [apiSummary, setApiSummary] = useState<{ totalSchools: number; totalStudents: number } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'attendance' | 'students'>('attendance');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 50,
    totalPages: 1
  });
  const itemsPerPage = 15;

  // Load data from API
  const loadData = useCallback(async () => {
    if (!roId || !fromDate || !toDate) return;

    setIsLoading(true);
    try {
      const response: ROWiseDataResponse = await attendanceApi.getROWiseKVData(
        parseInt(roId),
        1, // Fetch all data on page 1
        fromDate,
        toDate
      );

      if (response?.success && response.data && response.data.length > 0) {
        const regionData = response.data[0];
        setKvData(regionData.kvs || []);
        setApiSummary(response.summary || null);
        if (response.pagination) {
          setPagination(response.pagination);
        }
      } else {
        setKvData([]);
        setApiSummary(null);
      }
    } catch (error) {
      console.error('Error loading KV data:', error);
      setKvData([]);
    } finally {
      setIsLoading(false);
    }
  }, [roId, fromDate, toDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filtered and sorted data
  const filteredData = useMemo(() => {
    let result = [...kvData];

    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(kv =>
        kv.schoolName.toLowerCase().includes(query) ||
        kv.stateName.toLowerCase().includes(query)
      );
    }

    // Sort
    result.sort((a, b) => {
      let compareValue = 0;
      switch (sortBy) {
        case 'name':
          compareValue = a.schoolName.localeCompare(b.schoolName);
          break;
        case 'attendance':
          compareValue = a.attendancePercentage - b.attendancePercentage;
          break;
        case 'students':
          compareValue = a.totalEnrollment - b.totalEnrollment;
          break;
      }
      return sortOrder === 'asc' ? compareValue : -compareValue;
    });

    return result;
  }, [kvData, searchQuery, sortBy, sortOrder]);

  // Pagination
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(start, start + itemsPerPage);
  }, [filteredData, currentPage]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  // Summary stats
  const summaryStats = useMemo(() => {
    // Calculate working days from the unfiltered data (kvData)
    const schoolWithData = kvData.find(kv => kv.averagePresent > 0);
    const workingDays = schoolWithData
      ? (Math.round(schoolWithData.totalPresent / schoolWithData.averagePresent) || 1)
      : 1;

    // Use filtered data if search query exists, otherwise use all data
    const activeData = searchQuery ? filteredData : kvData;

    const totalStudents = activeData.reduce((sum, kv) => sum + kv.totalEnrollment, 0);
    const totalPresent = activeData.reduce((sum, kv) => sum + kv.totalPresent, 0);

    // Average present count divided by working days
    const averagePresentValue = workingDays > 0 ? Math.round(totalPresent) : 0;

    // Calculate average attendance
    let avgAttendance = 0;
    if (searchQuery) {
      avgAttendance = filteredData.length > 0
        ? filteredData.reduce((sum, kv) => sum + kv.attendancePercentage, 0) / filteredData.length
        : 0;
    } else if (apiSummary && apiSummary.totalStudents > 0) {
      // Use overall API summary to calculate average attendance
      const overallAvgPresent = workingDays > 0 ? (kvData.reduce((sum, kv) => sum + kv.totalPresent, 0) / workingDays) : 0;
      avgAttendance = (overallAvgPresent / apiSummary.totalStudents) * 100;
    } else {
      avgAttendance = kvData.length > 0
        ? kvData.reduce((sum, kv) => sum + kv.attendancePercentage, 0) / kvData.length
        : 0;
    }

    return {
      totalSchools: searchQuery ? filteredData.length : (apiSummary?.totalSchools ?? kvData.length),
      totalStudents: searchQuery ? totalStudents : (apiSummary?.totalStudents ?? totalStudents),
      present: averagePresentValue,
      avgAttendance: Math.round(avgAttendance * 10) / 10
    };
  }, [filteredData, kvData, searchQuery, apiSummary]);

  const getAttendanceColor = (percent: number) => {
    if (percent >= 90) return 'text-green-600 bg-green-50';
    if (percent >= 75) return 'text-blue-600 bg-blue-50';
    if (percent >= 60) return 'text-amber-600 bg-amber-50';
    return 'text-red-600 bg-red-50';
  };

  const getAttendanceBarColor = (percent: number) => {
    if (percent >= 90) return 'bg-green-500';
    if (percent >= 75) return 'bg-blue-500';
    if (percent >= 60) return 'bg-amber-500';
    return 'bg-red-500';
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
      <Header />
      <HeroCarousel images={carouselImages} autoRotateInterval={3000} />

      <div className="w-full max-w-7xl mx-auto px-4 -pt-2 -sm:pt-3">
        <Breadcrumb />
      </div>

      <main className="flex-grow w-full max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#6A1786] flex items-center gap-2">
              <Building2 className="w-7 h-7" />
              {roName} - KV Schools Attendance
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Student attendance data from {fromDate} to {toDate}
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              {/* Preset buttons */}
              <div className="flex items-center gap-1.5">
                {[7, 14, 30].map(days => {
                  const f = fromDate ? new Date(fromDate + 'T12:00:00') : null;
                  const t = toDate ? new Date(toDate + 'T12:00:00') : null;
                  const diffDays = f && t ? Math.round(Math.abs(t.getTime() - f.getTime()) / (1000 * 60 * 60 * 24)) : 0;
                  const isActive =
                    days === 7 ? diffDays === 7 :
                      days === 14 ? diffDays === 14 :
                        days === 30 ? (diffDays >= 28 && diffDays <= 31) : false;
                  return (
                    <button
                      key={days}
                      type="button"
                      onClick={() => {
                        const baseToDate = toDate ? new Date(toDate + 'T12:00:00') : new Date();
                        const pastDate = new Date(baseToDate);
                        pastDate.setDate(baseToDate.getDate() - days);

                        const formatDateStrLocal = (d: Date): string =>
                          `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

                        const newFromDate = formatDateStrLocal(pastDate);
                        const newToDate = formatDateStrLocal(baseToDate);

                        const newParams = new URLSearchParams(searchParams);
                        newParams.set('fromDate', newFromDate);
                        newParams.set('toDate', newToDate);
                        setSearchParams(newParams);
                      }}
                      className={`relative px-3 py-1.5 text-xs font-medium rounded-full transition-all duration-200 ${isActive
                          ? 'bg-[#6A1786] text-white shadow-sm shadow-purple-200 scale-105'
                          : 'bg-white border border-gray-200 text-gray-500 hover:bg-purple-50 hover:text-purple-700 hover:scale-105'
                        }`}
                    >
                      {days === 7 ? '7D' : days === 14 ? '2W' : '1M'}
                      {isActive && (
                        <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-pink-400 rounded-full animate-pulse" />
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Active range display chip */}
              {fromDate && toDate && (
                <div className="hidden sm:flex items-center gap-1.5 bg-purple-50 border border-purple-100 rounded-lg px-2.5 py-1.5">
                  <span className="text-xs text-purple-500 font-medium">
                    {fromDate} → {toDate}
                  </span>
                </div>
              )}

              {/* Max days warning */}
              <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 border border-amber-200 px-2.5 py-1.5 rounded-lg font-medium whitespace-nowrap">
                <AlertTriangle className="w-3.5 h-3.5" />
                Max {MAX_ANALYTICS_DATE_RANGE_DAYS} days
              </span>

              {/* DateRangePicker */}
              <DateRangePicker
                value={{ fromDate: fromDate || null, toDate: toDate || null }}
                onSubmit={(range) => {
                  if (range.fromDate && range.toDate) {
                    const newParams = new URLSearchParams(searchParams);
                    newParams.set('fromDate', range.fromDate);
                    newParams.set('toDate', range.toDate);
                    setSearchParams(newParams);
                  }
                }}
                disabled={isLoading}
              />
            </div>

            <button
              onClick={loadData}
              disabled={isLoading}
              className="p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-50"
              title="Refresh data"
            >
              <RefreshCw className={`w-5 h-5 text-gray-600 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => navigate('/attendance/regions')}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Regions
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100">
                <Building2 className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Schools</p>
                <p className="text-xl font-bold text-gray-900">{summaryStats.totalSchools}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <GraduationCap className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Students</p>
                <p className="text-xl font-bold text-gray-900">{summaryStats.totalStudents.toLocaleString()}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100">
                <Users className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Present</p>
                <p className="text-xl font-bold text-gray-900">{summaryStats.present.toLocaleString()}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100">
                <TrendingUp className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Avg. Attendance</p>
                <p className="text-xl font-bold text-gray-900">{summaryStats.avgAttendance}%</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by school name or state..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {/* Sort */}
            <div className="flex items-center gap-2">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="attendance">Sort by Attendance</option>
                <option value="name">Sort by Name</option>
                <option value="students">Sort by Students</option>
              </select>
              <button
                onClick={() => setSortOrder(o => o === 'asc' ? 'desc' : 'asc')}
                className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50"
                title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
              >
                {sortOrder === 'asc' ? (
                  <TrendingUp className="w-4 h-4 text-gray-600" />
                ) : (
                  <TrendingDown className="w-4 h-4 text-gray-600" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* School List */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <KVSLoader size={80} />
              <span className="mt-4 text-gray-600">Loading schools...</span>
            </div>
          ) : (
            <>
              {/* Table Header */}
              <div className="hidden md:grid grid-cols-10 gap-4 px-6 py-3 bg-gray-50 border-b border-gray-200 text-sm font-medium text-gray-600">
                <div className="col-span-4">School</div>
                <div className="col-span-2">Region</div>
                <div className="col-span-2 text-center">Students</div>
                <div className="col-span-2 text-center">Attendance</div>
              </div>

              {/* Table Body */}
              <div className="divide-y divide-gray-100">
                {paginatedData.map((kv, idx) => (
                  <button
                    key={kv.kvID}
                    onClick={() => navigate(`/attendance/student/analytics/kv/${kv.kvID}?fromDate=${fromDate}&toDate=${toDate}`, { state: { fromDate, toDate } })}
                    className="w-full grid grid-cols-1 md:grid-cols-10 gap-2 md:gap-4 px-6 py-4 hover:bg-gray-50 transition-colors text-left"
                  >
                    {/* School Name */}
                    <div className="md:col-span-4">
                      <div className="font-medium text-gray-900">{kv.schoolName}</div>
                      <div className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                        <MapPin className="w-3 h-3" />
                        {kv.stateName}
                      </div>
                    </div>

                    {/* Region */}
                    <div className="md:col-span-2 text-sm text-gray-600 flex items-center">
                      {roName}
                    </div>

                    {/* Students */}
                    <div className="md:col-span-2 text-center">
                      <div className="text-sm font-medium text-gray-900">{kv.totalEnrollment.toLocaleString()}</div>
                    </div>

                    {/* Attendance */}
                    <div className="md:col-span-2 flex flex-col items-center gap-1">
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getAttendanceColor(kv.attendancePercentage)}`}>
                        {kv.attendancePercentage.toFixed(2)}%
                      </span>
                      <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${getAttendanceBarColor(kv.attendancePercentage)}`}
                          style={{ width: `${kv.attendancePercentage}%` }}
                        />
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
                <div className="text-sm text-gray-600">
                  Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredData.length)} of {filteredData.length} schools
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg border border-gray-200 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="px-4 py-2 text-sm font-medium">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage >= totalPages}
                    className="p-2 rounded-lg border border-gray-200 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default StudentKVAttendanceList;
